import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import { ChannelType } from "@/modules/chat/gateway/channelTypes"
import { CHANNEL_LIST_SHOWN_WINDOW_WIDTH, DETAILS_OVERLAY_SHOWN_WINDOW_WIDTH } from "@/modules/chat/utils/chatConstants"
import { cn } from "@/modules/lib/utils"
import { ChatAvatar } from "@modules/chat/components/chatAvatar"
import { useChatPageContext } from "@modules/chat/contexts/chatPageContext"
import { getChannelCreatorId } from "@modules/chat/utils/getChannelCreatorId"
import { word } from "@modules/core/utils/i18n"
import { useWindowSize } from "@modules/utils/hooks/useWindowsSize"
import { getApproximatedFormattedNumber } from "@modules/utils/numberUtils"
import { InfoIcon, MenuIcon } from "lucide-react"
import { useEffect, useState } from "react"
import type { Event } from "stream-chat"
import {
  getDisplayTitle,
  useChannelActionContext,
  useChannelPreviewInfo,
  useChannelStateContext,
} from "stream-chat-react"
import { useChatClient } from "@/modules/chat/stores/useChatStore"

type ChannelHeaderProps = { className?: string } & React.ComponentProps<"div">

export function ChannelHeader({ className, ...props }: ChannelHeaderProps) {
  const chatClient = useChatClient()
  const { channel, watcher_count } = useChannelStateContext()
  const { displayImage } = useChannelPreviewInfo({ channel })
  const {
    displayChatDetailsOverlay,
    toggleDisplayChatDetailsOverlay,
    displayChannelList,
    toggleDisplayChannelList,
    messageToJumpTo,
    resetMessageToJumpTo,
  } = useChatPageContext()
  const { width } = useWindowSize()
  const [upToDateChannel, updateUpToDateChannel] = useState<{ name: string; member_count: number }>({
    name: getDisplayTitle(channel, chatClient?.user) ?? "",
    member_count: channel.data?.member_count ?? 0,
  })
  const { jumpToMessage } = useChannelActionContext()

  useEffect(() => {
    // workaround to deal with the real time changes
    const handleChannelUpdate = (e: Event) => {
      if (!e.channel || e.channel.member_count === undefined) return
      updateUpToDateChannel({
        name: getDisplayTitle(channel, chatClient?.user) || "",
        member_count: e.channel.member_count,
      })
    }
    chatClient?.on("channel.updated", handleChannelUpdate)

    return () => {
      chatClient?.off("channel.updated", handleChannelUpdate)
    }
  }, [chatClient, channel.data])

  useEffect(() => {
    if (messageToJumpTo) {
      jumpToMessage(messageToJumpTo).then(() => {
        resetMessageToJumpTo()
      })
    }
  }, [messageToJumpTo])

  if (!channel || !chatClient) return null

  const channelCreatorId = getChannelCreatorId(channel)
  const isCurrentUserGroupCreator = channelCreatorId === chatClient.user?.id
  const isProgramGroup = channel.type === ChannelType.PROGRAM
  const isProgramGroupMember = isProgramGroup && !isCurrentUserGroupCreator
  const isBoxGroupMember = channel.type === ChannelType.BOX && !isCurrentUserGroupCreator
  const isOneOnOneConversation =
    channel && Object.values(channel.state.members).length === 2 && channel.id?.indexOf("!members-") === 0
  const canUserSeeMemberList = isOneOnOneConversation || (!isProgramGroupMember && !isBoxGroupMember)
  const { approximatedFormattedNumber, approximatedNumber } = getApproximatedFormattedNumber(
    upToDateChannel.member_count,
  )

  return (
    <div
      className={cn(
        "str-chat__header-livestream str-chat__channel-header",
        { "rounded-tr-xl": !displayChatDetailsOverlay },
        { "rounded-tl-xl": !displayChannelList },
        className,
      )}
      {...props}
    >
      {width <= CHANNEL_LIST_SHOWN_WINDOW_WIDTH && (
        <Button variant="outline" size="iconMd" onClick={toggleDisplayChannelList}>
          <MenuIcon className="size-4" />
        </Button>
      )}

      <ChatAvatar image={displayImage} name={upToDateChannel.name} profileAvatarClassName="size-10" />

      <div className="str-chat__header-livestream-left str-chat__channel-header-end">
        <p className="str-chat__header-livestream-left--title str-chat__channel-header-title">
          <Text fontStyle="LL">{upToDateChannel.name}</Text>
        </p>
        <p className="str-chat__header-livestream-left--members str-chat__channel-header-info">
          <Text fontStyle="LM">
            {!!upToDateChannel.member_count && (
              <>
                {`${canUserSeeMemberList ? word("chat.members", { count: upToDateChannel.member_count }) : approximatedFormattedNumber + " " + word("global.member", { count: approximatedNumber })} ${
                  watcher_count ? " - " + watcher_count + " " + word("chat.online") : ""
                }`}
              </>
            )}
          </Text>
        </p>
      </div>
      {width <= DETAILS_OVERLAY_SHOWN_WINDOW_WIDTH && !displayChatDetailsOverlay && (
        <Button size="iconMd" variant="outline" onClick={toggleDisplayChatDetailsOverlay}>
          <InfoIcon className="size-4" />
        </Button>
      )}
    </div>
  )
}
